import React from "react";
import { ExerciseEditorProps } from "./exercise-editor-types";
import { MatchingExerciseContent } from "@org/ui";
import { Button } from "@org/ui";
import { Label } from "@org/ui";
import { Input } from "@org/ui";
import { XIcon, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const createPairId = () => `matching_pair_${Math.random().toString(36).slice(2, 10)}`;

interface SortablePairItemProps {
  pair: MatchingExerciseContent["data"]["pairs"][number];
  pairIndex: number;
  contentId: string;
  pairsLength: number;
  onPromptChange: (index: number, value: string) => void;
  onOptionChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

function SortablePairItem({
  pair,
  pairIndex,
  contentId,
  pairsLength,
  onPromptChange,
  onOptionChange,
  onRemove,
}: SortablePairItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pair.pairId ?? `pair-${pairIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border p-4 space-y-3 bg-background"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-grab text-muted-foreground hover:text-foreground touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
          <Label>Pair {pairIndex + 1}</Label>
        </div>
        {pairsLength > 2 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-red-500"
            onClick={() => onRemove(pairIndex)}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`prompt-${contentId}-${pairIndex}`}>
            Prompt (Left Column)
          </Label>
          <Input
            id={`prompt-${contentId}-${pairIndex}`}
            value={pair.promptLabel}
            onChange={(event) => onPromptChange(pairIndex, event.target.value)}
            placeholder="Enter prompt text..."
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor={`option-${contentId}-${pairIndex}`}>
            Match (Right Column)
          </Label>
          <Input
            id={`option-${contentId}-${pairIndex}`}
            value={pair.optionLabel}
            onChange={(event) => onOptionChange(pairIndex, event.target.value)}
            placeholder="Enter matching text..."
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
}

export const MatchingExerciseEditor: React.FC<ExerciseEditorProps<MatchingExerciseContent>> = ({
  content,
  updateContent,
}) => {
  const pairs = content.data.pairs ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const applyPairs = (nextPairs: MatchingExerciseContent["data"]["pairs"]) => {
    const normalized = nextPairs.map((pair, index) => ({
      ...pair,
      pairId: pair.pairId ?? createPairId(),
      order: index,
    }));

    updateContent({
      ...content,
      data: {
        ...content.data,
        pairs: normalized,
      },
    });
  };

  const handlePromptChange = (index: number, value: string) => {
    applyPairs(
      pairs.map((pair, idx) =>
        idx === index ? { ...pair, promptLabel: value } : pair
      )
    );
  };

  const handleOptionChange = (index: number, value: string) => {
    applyPairs(
      pairs.map((pair, idx) =>
        idx === index ? { ...pair, optionLabel: value } : pair
      )
    );
  };

  const handleAddPair = () => {
    applyPairs([
      ...pairs,
      {
        pairId: createPairId(),
        promptLabel: "",
        optionLabel: "",
        order: pairs.length,
      },
    ]);
  };

  const handleRemovePair = (index: number) => {
    if (pairs.length <= 2) return;
    applyPairs(pairs.filter((_, idx) => idx !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pairs.findIndex(
        (pair) => (pair.pairId ?? `pair-${pairs.indexOf(pair)}`) === active.id
      );
      const newIndex = pairs.findIndex(
        (pair) => (pair.pairId ?? `pair-${pairs.indexOf(pair)}`) === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        applyPairs(arrayMove(pairs, oldIndex, newIndex));
      }
    }
  };

  const pairIds = pairs.map((pair, index) => pair.pairId ?? `pair-${index}`);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-dashed border-muted-foreground/50 bg-muted/30 p-3">
        <p className="text-sm text-muted-foreground">
          Create pairs of prompts and matches. Students will drag options from
          the right column to connect them with the correct prompts on the left.
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={pairIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {pairs.map((pair, pairIndex) => (
              <SortablePairItem
                key={pair.pairId ?? `pair-${pairIndex}`}
                pair={pair}
                pairIndex={pairIndex}
                contentId={content.id}
                pairsLength={pairs.length}
                onPromptChange={handlePromptChange}
                onOptionChange={handleOptionChange}
                onRemove={handleRemovePair}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button variant="outline" size="sm" onClick={handleAddPair}>
        Add Pair
      </Button>
    </div>
  );
};
